import React, { useState, useEffect } from 'react';

import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';

import FormContainer from '../../../FormContainer';
import AddOrEditPageTabs from '../../../AddOrEditPageTabs';
import Table from '../../../../Table';
import Alert from '../../../../Alert';

import useImpoundAnalysisForm from '../../../../../forms/loan/impoundAnalysis';
import { useImpoundsStore } from '../../../../../store';
import LoanService from '../../../../../services/LoanService';
import useInvoices from '../Impounds/hooks/useInvoices';
import getImpoundAnalysisData from './helpers/getImpoundAnalysisData';
import useQueryParam from '../../../../../hooks/useQueryParam';
import getImpoundAnalysisTableData from './helpers/getImpoundAnalysisTableData';
import ImpoundAnalysisService from '../../../../../services/ImpoundAnalysisService';
import { useUiContext } from '../../../../../context/UiContext';
import reconstructRowDescription from './helpers/reconstructRowDescription';

const hasBillsOfCerainType = (bills, type) => {
  return bills.some((bill) =>
    bill.descriptionData.some((descData) =>
      descData.description.startsWith(type)
    )
  );
};

const createAlert = (loan, bills) => {
  if (!loan) {
    return '';
  }

  if (loan.tax_impound && !hasBillsOfCerainType(bills, 'Tax')) {
    return 'This loan is escrowing taxes, but the impound analysis has no tax bills listed. This could cause an unexpected shortage.';
  }

  if (loan.insurance_impound && !hasBillsOfCerainType(bills, 'Insurance')) {
    return 'This loan is escrowing insurance, but the impound analysis has no insurance bills listed. This could cause an unexpected shortage.';
  }

  if (loan.hoa_impound && !hasBillsOfCerainType(bills, 'HOA')) {
    return 'This loan is escrowing HOA fees, but the impound analysis has no HOA bills listed. This could cause an unexpected shortage.';
  }

  return '';
};

const cols = [
  {
    title: 'Period',
    keys: ['period'],
    type: 'date',
  },
  {
    title: 'Impound Payments',
    keys: ['impoundPayments'],
    type: 'currency',
  },
  {
    title: 'Distribution',
    keys: ['distribution'],
    type: 'text',
  },
  {
    title: 'Description',
    keys: ['description'],
  },
  {
    title: 'Calculated Balance',
    keys: ['calculatedBalance'],
    type: 'currency',
  },
  {
    title: 'Final Balance',
    keys: ['finalBalance'],
    type: 'currency',
  },
];

export default function ImpoundAnalysis({ loanId, loan }) {
  const [analysisData, setAnalysisData] = useState(null);
  const [impoundCalculationRows, setImpoundCalculationRows] = useState([]);

  const { showToast } = useUiContext();

  const { id, resourceId } = useParams();
  const navigate = useNavigate();

  const { getQueryParam } = useQueryParam('invoiceId');

  const invoicesFromStore = useImpoundsStore((state) => state.invoices);
  const bills = useImpoundsStore((state) => state.bills);

  const { data: impoundSchedule } = useQuery({
    queryKey: ['impoundSchedule', loanId],
    queryFn: () =>
      LoanService.getLoanPaymentSchedule({ loanId, type: 'impound' }),
    enabled: !!(loanId && !invoicesFromStore.length),
  });

  const { data: impoundAnalysis } = useQuery({
    queryKey: ['impoundAnalysis', resourceId],
    queryFn: () => ImpoundAnalysisService.getImpoundAnalysis(resourceId),
    enabled: !!resourceId,
  });

  const sortedInvoices = useInvoices(
    impoundSchedule || [],
    impoundSchedule?._bills || []
  );

  const invoices = invoicesFromStore.length
    ? invoicesFromStore
    : sortedInvoices;

  useEffect(() => {
    if (invoices && invoices.length && !resourceId) {
      const data = getImpoundAnalysisData({
        invoiceId: getQueryParam(),
        invoices,
        bills: bills?.length ? bills : impoundSchedule?._bills,
        loanCushion: loan?.impound_cushion,
      });

      const tableData = getImpoundAnalysisTableData({
        invoices,
        startingInvoiceIndex: data.startingInvoiceIndex,
        totalMonthlyImpoundPayment: data.totalMonthlyImpoundPayment,
        bills: bills?.length ? bills : impoundSchedule?._bills,
        initialImpoundBalance: data.initialImpoundBalance,
        calculatedBalance: data.calculatedBalance,
        adjustment: data.adjustment,
      });

      setAnalysisData(data);
      setImpoundCalculationRows(
        tableData.map((row) => ({ ...row, style: 'align-top' }))
      );
    } else if (impoundAnalysis) {
      setAnalysisData(impoundAnalysis.calculation_table.formValues);

      setImpoundCalculationRows(
        impoundAnalysis.calculation_table.tableData.map((row) => {
          if (!row.descriptionData && !row.descriptionData.length) {
            return row;
          }

          return {
            ...row,
            description: reconstructRowDescription(row.descriptionData),
            style: 'align-top',
          };
        })
      );
    }
  }, [
    bills,
    getQueryParam,
    id,
    impoundAnalysis,
    impoundSchedule?._bills,
    invoices,
    loan?.impound_cushion,
    resourceId,
  ]);

  const tabs = [
    {
      label: 'Schedule',
      activeOn: 'Impounds',
      onClick: () => navigate(`/loans/${id}?tab=Impounds`),
    },
    {
      label: 'Analysis',
      activeOn: ['Start Analysis', 'Impound Analysis'],
    },
    {
      label: 'Initial',
    },
  ];

  const form = useImpoundAnalysisForm(
    () => {},
    {
      startingInvoice: analysisData?.startingInvoice?.date,
      initialImpoundBalance: analysisData?.initialImpoundBalance,
      totalMonthlyImpoundPayment: analysisData?.totalMonthlyImpoundPayment,
      taxPayment: analysisData?.taxPayment,
      insurancePayment: analysisData?.insurancePayment,
      hoaPayment: analysisData?.hoaPayment,
      totalCushion: analysisData?.totalCushion,
      adjustment: analysisData?.adjustment,
    },
    analysisData?.startingInvoice,
    loan?.impound_cushion
  );

  const { mutate: createImpoundAnalysis, isPending } = useMutation({
    mutationFn: () => {
      const data = {
        loanId,
        startingInvoiceId: analysisData?.startingInvoice?.id,
        initialImpoundBalance: analysisData?.initialImpoundBalance,
        previousInsuranceAmount: loan?.impound_insurance,
        previousTaxAmount: loan?.impound_tax,
        previousHoaAmount: loan?.impound_hoa,
        taxPayment: analysisData?.taxPayment,
        insurancePayment: analysisData?.insurancePayment,
        hoaPayment: analysisData?.hoaPayment,
        totalCushion: analysisData?.totalCushion,
        adjustment: analysisData?.adjustment,
        calculatedData: {
          formValues: analysisData,
          tableData: impoundCalculationRows.map((row) => {
            // eslint-disable-next-line no-shadow
            const data = { ...row };

            delete data.description;
            delete data.style;

            return data;
          }),
        },
      };

      return ImpoundAnalysisService.createImpoundAnalysis(data);
    },
    // eslint-disable-next-line no-shadow
    onSuccess: (impoundAnalysis) => {
      showToast({
        text: 'Success',
        type: 'success',
      });
      navigate(
        `/loans/${id}/impound-analysis/${impoundAnalysis.id}?tab=Impound Analysis`
      );
    },
    onError: (error) => {
      console.log(error);
      showToast({
        text: error?.response?.data?.message || 'Something went wrong',
        type: 'error',
      });
    },
  });

  const alert = createAlert(loan, impoundCalculationRows);

  return (
    <div>
      <div className="mb-4 flex justify-between md:items-center flex-col md:flex-row gap-2 h-[37px]">
        <AddOrEditPageTabs tabs={tabs} />
      </div>
      <div className="p-7 col-span-full sm:col-span-6 xl:col-span-4 bg-white dark:bg-slate-800 shadow-lg rounded-sm border border-slate-200 dark:border-slate-700">
        <div className="mb-10">
          <FormContainer
            form={form}
            noBillComId
            noSubmit
            headerRight={
              !resourceId && (
                <div className="flex gap-5 md:gap-10 md:flex-row flex-col items-center">
                  <button
                    disabled={isPending}
                    type="button"
                    className="btn dark:bg-slate-800 border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600 text-indigo-500"
                    onClick={createImpoundAnalysis}
                  >
                    Generate
                  </button>
                </div>
              )
            }
          />
        </div>
        <h2 className="text-xl text-slate-800 dark:text-slate-100 font-bold flex justify-between w-full mb-6">
          Impound Calculation
        </h2>
        {alert && (
          <div className="mb-4">
            <Alert message={alert} />
          </div>
        )}
        <Table cols={cols} rows={impoundCalculationRows} title="Invoices" />
      </div>
    </div>
  );
}
